import {
  Box,
  Flex,
  HStack,
  Image,
  Text,
  Tooltip,
  Tag,
  useColorModeValue,
  Grid,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';

import Skeleton from 'ui/shared/chakra/Skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import IconSvg from 'ui/shared/IconSvg';
import LinkExternal from 'ui/shared/links/LinkExternal';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';

type Task = {
  _id: string;
  address: string;
  source_address: string;
  source_info: {
    client: {
      client_address: string;
    };
    image_content: {
      compose1: string;
      compose2: string;
      compose3: string;
      compose4: string;
      name: string;
      description: string;
      logo_url: string;
      tags: Array<string>;
    };
    source_constraints: {
      maxPayment: number;
      minPayment: number;
      minAvailableStake: number;
      maxDuration: number;
      minTier: number;
    };
    _id: string;
    nexus_address: string;
    source_address: string;
    image_metadata_url: string;
    source_constraints_url: string;
    registered_providers: Array<{
      provider_address: string;
      _id: string;
    }>;
    tasks: Array<{
      task_address: string;
      _id: string;
    }>;
    status: string;
    timestamp: number;
    createdAt: string;
    updatedAt: string;
  };
  expiry_time: number;
  config_url: string;
  payment: string;
  offered_reward: string;
  attempted_providers: Array<{
    response?: {
      response_address: string;
    };
    provider_address: string;
    timestamp: number;
    _id: string;
    response_data?: {
      _id: string;
      response_address: string;
      task_address: string;
      provider_address: string;
      timestamp: number;
      response_url: string;
      status: string;
      createdAt: string;
      updatedAt: string;
    };
  }>;
  status: string;
  timestamp: number;
  createdAt: string;
  updatedAt: string;
};

type Props = {
  task: Task;
  isLoading?: boolean;
};

const LatestTasksItem = ({ task, isLoading }: Props) => {
  // Get up to 3 providers with responses

  // Check if task is expired
  const isExpired = task.expiry_time < Date.now();

  // Text to display for expiry status
  const expiryText = isExpired ? 'Expired' : 'Expires';

  // Get color and icon based on status
  const getStatusConfig = () => {
    switch (task.status) {
      case 'New':
        return {
          colorScheme: 'blue',
          icon: 'status/pending' as const,
          text: 'New',
        };
      case 'Attempted':
        return {
          colorScheme: 'orange',
          icon: 'status/pending' as const,
          text: 'Attempted',
        };
      case 'Completed':
        return {
          colorScheme: 'green',
          icon: 'status/success' as const,
          text: 'Completed',
        };
      case 'Responded':
        return {
          colorScheme: 'green',
          icon: 'status/success' as const,
          text: 'Responded',
        };
      case 'Expired':
        return {
          colorScheme: 'red',
          icon: 'status/error' as const,
          text: 'Expired',
        };
      case 'Finalized':
        return {
          colorScheme: 'green',
          icon: 'status/success' as const,
          text: 'Finalized',
        };
      default:
        return {
          colorScheme: 'gray',
          icon: 'status/pending' as const,
          text: task.status,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const sourceBgColor = useColorModeValue('gray.50', 'gray.800');
  const sourceUrl = `https://management.opengpu.network/sources/${ task.source_info.source_address }`;

  const formatPayment = (payment: string) => {
    const num = Number(payment);
    if (isNaN(num)) return '0';
    const decimalPlaces = (num.toString().split('.')[1] || '').length;
    return decimalPlaces > 4 ? num.toFixed(4) : num.toString();
  };

  return (
    <Box
      as={ motion.div }
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ display: 'none' }}
      transitionDuration="normal"
      transitionTimingFunction="linear"
      width="100%"
      borderTop="1px solid"
      borderColor="divider"
      py={ 4 }
      _last={{ borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Flex justifyContent="space-between" gap={ 2 }>
        <HStack flexWrap="wrap" spacing={ 3 }>
          <LinkExternal
            href={ sourceUrl }
            display="flex"
            alignItems="center"
            bg={ sourceBgColor }
            borderRadius="4px"
            paddingX={ 2 }
            height="24px"
            _hover={{
              textDecoration: 'none',
              bg: useColorModeValue('gray.100', 'gray.700'),
            }}
          >
            { task.source_info?.image_content?.logo_url ? (
              <Image
                src={ task.source_info.image_content.logo_url }
                alt={ task.source_info.image_content?.name || 'Unknown' }
                boxSize="20px"
                borderRadius="md"
                mr={ 2 }
                objectFit="cover"
              />
            ) : (
              <IconSvg
                name="block_slim"
                boxSize={ 5 }
                mr={ 2 }
                p="1px"
                isLoading={ isLoading }
                flexShrink={ 0 }
              />
            ) }
            <Tooltip label={ task.source_info?.image_content?.name || 'Unknown' }>
              <Text
                fontSize="xs"
                fontWeight={ 500 }
                maxW="120px"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                { task.source_info?.image_content?.name || 'Unknown' }
              </Text>
            </Tooltip>
          </LinkExternal>
          <Tag
            size="sm"
            colorScheme={ statusConfig.colorScheme }
            borderRadius="4px"
            variant="subtle"
            display="flex"
            alignItems="center"
            height="24px"
            paddingX={ 2 }
          >
            <Box display="flex" alignItems="center">
              <IconSvg name={ statusConfig.icon } boxSize="12px" mr={ 1 }/>
              <Text>{ statusConfig.text }</Text>
            </Box>
          </Tag>
        </HStack>
      </Flex>

      <Flex
        mt={ 2 }
        alignItems="center"
        width="100%"
        justifyContent="space-between"
        mb={ 4 }
      >
        <AddressEntity
          address={{ hash: task.address }}
          isLoading={ isLoading }
          fontWeight="700"
          truncation="constant_long"
        />
        <TimeAgoWithTooltip
          timestamp={ task.timestamp }
          enableIncrement
          isLoading={ isLoading }
          color="text_secondary"
          fontWeight="400"
          fontSize="sm"
          ml={ 3 }
        />
      </Flex>

      { /* Provider section with slider */ }
      { /* <Box mb={ 3 }>
        { providersWithResponses.length > 0 ? (
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={ 2 }>
              <Text fontSize="sm" fontWeight="500">
                Provider { currentProviderIndex + 1 } of{ ' ' }
                { providersWithResponses.length }
              </Text>
              <HStack spacing={ 1 }>
                <IconButton
                  aria-label="Next provider"
                  icon={ <IconSvg name="arrows/east-mini" boxSize={ 4 }/> }
                  size="xs"
                  variant="ghost"
                  isDisabled={ providersWithResponses.length <= 1 }
                  onClick={ handleNextProvider }
                />
                <IconButton
                  aria-label="Previous provider"
                  icon={ (
                    <IconSvg
                      name="arrows/east-mini"
                      transform="rotate(180deg)"
                      boxSize={ 4 }
                    />
                  ) }
                  size="xs"
                  variant="ghost"
                  isDisabled={ providersWithResponses.length <= 1 }
                  onClick={ handlePrevProvider }
                />
              </HStack>
            </Flex>

            <Grid
              templateColumns="minmax(0, 1fr) auto minmax(0, 1fr)"
              alignItems="center"
              columnGap={ 2 }
            >
              <Text fontSize="xs" color="text_secondary">
                Provider
              </Text>
              <Box/>
              <Text fontSize="xs" color="text_secondary" ml={ 1 }>
                Response
              </Text>

              { providersWithResponses[currentProviderIndex]?.response &&
              providersWithResponses[currentProviderIndex].response
                .response_address !== ZERO_ADDRESS ? (
                  <>
                    <AddressEntity
                      address={{
                        hash: providersWithResponses[currentProviderIndex]
                          .provider_address,
                        private_tags: null,
                        public_tags: null,
                        watchlist_names: null,
                        implementations: null,
                        name: null,
                        is_contract: false,
                        is_verified: null,
                        ens_domain_name: null,
                      }}
                      isLoading={ isLoading }
                      fontSize="sm"
                      truncation="constant"
                    />
                    <IconSvg name="arrows/east" boxSize={ 4 } color="gray.400"/>
                    <AddressEntity
                      address={{
                        hash: providersWithResponses[currentProviderIndex]
                          .response.response_address,
                        private_tags: null,
                        public_tags: null,
                        watchlist_names: null,
                        implementations: null,
                        name: null,
                        is_contract: false,
                        is_verified: null,
                        ens_domain_name: null,
                      }}
                      isLoading={ isLoading }
                      fontSize="sm"
                      truncation="constant"
                    />
                  </>
                ) : (
                  <>
                    <AddressEntity
                      address={{
                        hash: providersWithResponses[currentProviderIndex]
                          .provider_address,
                        private_tags: null,
                        public_tags: null,
                        watchlist_names: null,
                        implementations: null,
                        name: null,
                        is_contract: false,
                        is_verified: null,
                        ens_domain_name: null,
                      }}
                      isLoading={ isLoading }
                      fontSize="sm"
                      truncation="constant"
                    />
                    <IconSvg name="arrows/east" boxSize={ 4 } color="gray.400"/>
                    <Text fontSize="sm" color="text_secondary">
                      Awaiting response
                    </Text>
                  </>
                ) }
            </Grid>
          </Box>
        ) : (
          <Flex direction="column" mb={ 2 }>
            <Text fontSize="sm" fontWeight="500">
              Providers
            </Text>
            <Text fontSize="xs" color="text_secondary">
              Waiting for providers
            </Text>
          </Flex>
        ) }
      </Box> */ }

      <Grid templateColumns="1fr 1fr">
        { /* Payment section styled like Value */ }
        <Skeleton isLoaded={ !isLoading } mb={ 2 } fontSize="sm" w="fit-content">
          <Text as="span">Payment </Text>
          <Text as="span" variant="secondary">
            { formatPayment(task.offered_reward || '0') } OGPU
          </Text>
        </Skeleton>

        { /* Expires section styled like Fee */ }
        <Skeleton
          isLoaded={ !isLoading }
          fontSize="sm"
          w="fit-content"
          display="flex"
          whiteSpace="pre"
        >
          <Text as="span">{ expiryText } </Text>
          <TimeAgoWithTooltip
            timestamp={ task.expiry_time }
            isLoading={ isLoading }
            color="text_secondary"
            fontSize="sm"
          />
        </Skeleton>
      </Grid>
    </Box>
  );
};

export default LatestTasksItem;
